<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { useDefaultToast } from "@/composables/useDefaultToast";
import { useAuthStore } from "@/modules/auth/store/authStore";
import { getNonDelegableInactivatedReasons } from "@/modules/worker/helpers/inactivatedReasons";
import workerService from "@/modules/worker/services/workerService";
import { useConfigurationStore } from "@/store/configurationStore";

import type { NonDelegableFlowResult } from "@/components/modal/Profile/nonDelegable/types";
import type { SelectOption } from "@/dsComponents/inputs/types";

import Alert from "@/dsComponents/Alert/Alert.vue";
import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import Select from "@/dsComponents/inputs/Select.vue";
import TextareaInput from "@/dsComponents/inputs/TextareaInput.vue";
import Modal from "@/dsComponents/modals/Modal.vue";

import NonDelegableFlowModals from "@/components/modal/Profile/nonDelegable/NonDelegableFlowModals.vue";
import rejectProfileService from "@/components/modal/Profile/rejectProfile/services/rejectProfileService";
import { RejectionType, type RejectProfileWorkerModalProps, type RejectReason } from "@/components/modal/Profile/rejectProfile/types";

const { t } = useI18n();
const { toastError, toastSuccess } = useDefaultToast();
const authStore = useAuthStore();

const props = withDefaults(defineProps<RejectProfileWorkerModalProps>(), {
    includeCompanyNeedRejection: false,
});

const emit = defineEmits<(e: "close") => void>();

const rejectionType = ref<RejectionType>(props.includeCompanyNeedRejection ? RejectionType.FOR_NEED : RejectionType.GLOBAL_NOT_DELEGABLE);
const reason = ref<string>("");
const comment = ref<string>("");
const reasonError = ref<boolean>(false);
const commentError = ref<boolean>(false);
const isNonDelegableFlowOpen = ref(false);
const canSetNonDelegable = ref(true);
const isResolvingAgencyMembership = ref(false);

const rejectionTypeOptions = computed(() => {
    const options = [
        { label: t("modals.companyNeed.rejectProfile.fields.rejectionType.options.forNeed"), value: RejectionType.FOR_NEED },
    ];

    if (canSetNonDelegable.value) {
        options.push({
            label: t("modals.companyNeed.rejectProfile.fields.rejectionType.options.globalNotDelegable"),
            value: RejectionType.GLOBAL_NOT_DELEGABLE,
        });
    }

    return options;
});

const reasonOptions = ref<(SelectOption & { requiresComment?: boolean })[]>([]);
const isLoadingReject = ref(false);
const isLoadingReasons = ref(false);

const selectedReason = computed(() => reasonOptions.value.find((option) => option.value === reason.value));
const isCommentRequired = computed(() => selectedReason.value?.requiresComment ?? false);
const showForNeedWarning = computed(() => props.includeCompanyNeedRejection && rejectionType.value === RejectionType.FOR_NEED);

const handleClose = () => emit("close");

const fetchRejectReasons = async (rejectionType: RejectionType): Promise<RejectReason[]> => {
    const configurationStore = useConfigurationStore();

    if (rejectionType === "global-not-delegable") {
        return getNonDelegableInactivatedReasons(configurationStore.configuration.inactivatedReasons).map((item) => ({
            id: item.id,
            label: item.reason,
        }));
    }

    return configurationStore.configuration.rejectedCompanyNeedReasons.map((item) => ({
        id: item.id,
        label: item.label,
    }));
};

const fetchReasons = async (type: RejectionType) => {
    isLoadingReasons.value = true;
    try {
        const reasons = await fetchRejectReasons(type);
        reasonOptions.value = reasons.map((reasonItem) => ({
            label: reasonItem.label,
            value: reasonItem.id,
            requiresComment: reasonItem.requiresComment,
        }));
    } finally {
        isLoadingReasons.value = false;
    }
};

const handleRejectionTypeSelect = async (option: SelectOption) => {
    reason.value = "";
    reasonError.value = false;
    commentError.value = false;
    await fetchReasons(option.value as RejectionType);
};

const handleReasonSelect = (option: SelectOption) => {
    reason.value = option.value;
    reasonError.value = false;
    commentError.value = false;
};

const handleCommentUpdate = (value: string) => {
    comment.value = value;
    if (value.trim()) {
        commentError.value = false;
    }
};

const validateForm = () => {
    if (!reason.value) {
        reasonError.value = true;
        return false;
    }
    if (isCommentRequired.value && !comment.value.trim()) {
        commentError.value = true;
        return false;
    }
    return true;
};

const handleRejectForNeed = async () => {
    isLoadingReject.value = true;
    try {
        const result = await rejectProfileService.rejectProfilesForNeed(props.companyNeedId, {
            workers: props.workersIds.map((workerId) => ({ workerId, fromJobOffer: props.fromJobOffer ?? false })),
            rejectionReasonId: reason.value,
            rejectionComment: comment.value,
        });
        if (props.onSuccess) {
            await props.onSuccess({
                successCount: result.rejectedCount,
                rejectionType: rejectionType.value,
                reasonId: reason.value,
                reasonLabel: selectedReason.value?.label ?? "",
                comment: comment.value,
            });
        }
        handleClose();
    } catch (error) {
        console.error(error);
        toastError(t("modals.companyNeed.moveWorker.refuseWorkers.error", { count: props.workersIds.length }));
    } finally {
        isLoadingReject.value = false;
    }
};

const handleReject = async () => {
    if (!validateForm()) return;

    if (rejectionType.value === RejectionType.GLOBAL_NOT_DELEGABLE) {
        isNonDelegableFlowOpen.value = true;
        return;
    }

    await handleRejectForNeed();
};

const handleNonDelegableFlowComplete = async (result: NonDelegableFlowResult) => {
    if (result.action === "remove-from-agency") {
        if (props.onRemoveFromAgency) {
            await props.onRemoveFromAgency(result.workerIds);
        }
        toastSuccess(t("page.profile.removeFromAgency.success"));
        handleClose();
        return;
    }

    if (props.onSuccess) {
        await props.onSuccess({
            successCount: result.workerIds.length,
            rejectionType: RejectionType.GLOBAL_NOT_DELEGABLE,
            reasonId: result.reasonId,
            reasonLabel: selectedReason.value?.label ?? "",
            comment: result.comment,
        });
    }
    handleClose();
};

const isWorkerInCurrentAgency = (agencyIds: string[] | undefined, currentAgencyId: string | undefined): boolean => {
    if (!currentAgencyId) return false;
    return (agencyIds ?? []).includes(currentAgencyId);
};

const resolveCanSetNonDelegable = async (): Promise<boolean> => {
    if (props.canSetNonDelegable !== undefined) {
        return props.canSetNonDelegable;
    }

    if (!props.includeCompanyNeedRejection || props.workersIds.length === 0) {
        return true;
    }

    const currentAgencyId = authStore.currentAgency?.id;
    if (!currentAgencyId) {
        return false;
    }

    try {
        const profiles = await Promise.all(props.workersIds.map((workerId) => workerService.getProfilePreview(workerId)));
        return profiles.every((profile) => isWorkerInCurrentAgency(profile.agencies?.map((agency) => agency.id), currentAgencyId));
    } catch {
        return false;
    }
};

const initializeRejectionModal = async () => {
    if (props.includeCompanyNeedRejection) {
        isResolvingAgencyMembership.value = true;
        try {
            canSetNonDelegable.value = await resolveCanSetNonDelegable();
            if (!canSetNonDelegable.value) {
                rejectionType.value = RejectionType.FOR_NEED;
            }
        } finally {
            isResolvingAgencyMembership.value = false;
        }
    }

    await fetchReasons(rejectionType.value);
};

watch(canSetNonDelegable, (isAllowed) => {
    if (!isAllowed && rejectionType.value === RejectionType.GLOBAL_NOT_DELEGABLE) {
        rejectionType.value = RejectionType.FOR_NEED;
        void fetchReasons(RejectionType.FOR_NEED);
    }
});

onBeforeMount(() => {
    void initializeRejectionModal();
});
</script>

<template>
    <Modal
        class="reject-profile-worker-modal"
        width="500px"
        :title="t('modals.companyNeed.rejectProfile.title')"
        :open="props.open"
        close
        @close="handleClose"
    >
        <template #default>
            <div class="reject-profile-worker-modal__content">
                <Select
                    v-if="props.includeCompanyNeedRejection"
                    v-model="rejectionType"
                    size="xs"
                    :label="t('modals.companyNeed.rejectProfile.fields.rejectionType.label')"
                    width="100%"
                    :options="rejectionTypeOptions"
                    icon="selector"
                    :disabled="isResolvingAgencyMembership"
                    @select="handleRejectionTypeSelect"
                />
                <Select
                    v-model="reason"
                    size="xs"
                    :label="t('modals.companyNeed.rejectProfile.fields.reason.label')"
                    :placeholder="t('modals.companyNeed.rejectProfile.fields.reason.placeholder')"
                    width="100%"
                    :options="reasonOptions"
                    icon="selector"
                    required
                    :disabled="isLoadingReasons"
                    :error="reasonError ? 'error' : undefined"
                    @select="handleReasonSelect"
                />
                <TextareaInput
                    :modelValue="comment"
                    :label="t('modals.companyNeed.rejectProfile.fields.comment.label')"
                    :placeholder="t('modals.companyNeed.rejectProfile.fields.comment.placeholder')"
                    :rows="3"
                    :required="isCommentRequired"
                    :error="commentError ? t('modals.companyNeed.rejectProfile.fields.comment.errorRequired') : undefined"
                    no-padding
                    @update:modelValue="handleCommentUpdate"
                />

                <Alert
                    v-if="showForNeedWarning"
                    :message="t('modals.companyNeed.rejectProfile.missionProposedWarning.forNeed')"
                    icon="alert-triangle"
                    variant="red"
                />
            </div>
        </template>
        <template #footer>
            <div class="reject-profile-worker-modal__footer">
                <ButtonAlt type="neutral" variant="ghost" :label="t('global.actions.cancel')" @click="handleClose" />
                <ButtonAlt
                    type="error"
                    variant="filled"
                    :label="t('global.actions.refuse')"
                    @click="handleReject"
                    :loading="isLoadingReject"
                />
            </div>
        </template>
    </Modal>

    <NonDelegableFlowModals
        :open="isNonDelegableFlowOpen"
        :worker-ids="props.workersIds"
        :worker-name="props.workerName"
        :reason-id="reason"
        :comment="comment"
        @close="isNonDelegableFlowOpen = false"
        @complete="handleNonDelegableFlowComplete"
    />
</template>

<style scoped lang="scss">
.reject-profile-worker-modal {
    &__content {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3xl, 24px);
    }

    &__footer {
        display: flex;
        justify-content: flex-end;
        gap: var(--spacing-lg, 12px);
    }
}
</style>
